import { useCallback, useState } from 'react';
import type { ImportPage } from 'services/imports/fileToPages';
import { MAX_SELECTED_PAGES } from 'services/imports/limits';

/** A page can be chosen for extraction when it rendered. Extracted pages can be extracted again. */
export const isSelectablePage = (page: ImportPage): boolean => !page.failed;

/** Selected pages that still count against MAX_SELECTED_PAGES. */
export const countPendingSelected = (pages: readonly ImportPage[], selectedPageIds: ReadonlySet<string>): number => (
  pages.filter((page) => selectedPageIds.has(page.id) && isSelectablePage(page)).length
);

/** Pages chosen for text extraction. New pages are never selected automatically. */
export const usePageSelection = (pages: readonly ImportPage[]) => {
  const [selectedPageIds, setSelectedPageIds] = useState<ReadonlySet<string>>(() => new Set());

  // Deselecting is always allowed; selecting needs a selectable page and room under the cap.
  const togglePageSelected = useCallback((pageId: string) => {
    setSelectedPageIds((prev) => {
      if (prev.has(pageId)) {
        const next = new Set(prev);
        next.delete(pageId);
        return next;
      }
      const page = pages.find((candidate) => candidate.id === pageId);
      const hasRoom = countPendingSelected(pages, prev) < MAX_SELECTED_PAGES;
      if (!page || !isSelectablePage(page) || !hasRoom) return prev;
      return new Set(prev).add(pageId);
    });
  }, [pages]);

  const clearSelection = useCallback(() => setSelectedPageIds(new Set()), []);

  /** Successfully extracted pages leave the selection; pages that failed or were cancelled stay for a retry. */
  const releaseExtracted = useCallback((extracted: ReadonlySet<string>) => {
    setSelectedPageIds((prev) => new Set(Array.from(prev).filter((pageId) => !extracted.has(pageId))));
  }, []);

  return {
    selectedPageIds,
    selectedCount: countPendingSelected(pages, selectedPageIds),
    togglePageSelected,
    clearSelection,
    releaseExtracted,
  };
};
